import { Typography } from "@/components/ui/Typography/Typography";
import { cn } from "@/lib/utils/cn";
import { useEffect, useRef, useState } from "react";

interface ExpandableTextProps {
  text: string;
  maxLines?: number;
  size?: "default" | "xs" | "sm" | "md" | "lg" | "xl" | "xxl";
  className?: string;
}

export const ExpandableText = ({
  text,
  maxLines = 3,
  size = "sm",
  className,
}: ExpandableTextProps) => {
  const [isTextExpanded, setIsTextExpanded] = useState(false);
  const [showTextButton, setShowTextButton] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (measureRef.current && textRef.current) {
      const measureElement = measureRef.current;
      const displayElement = textRef.current;

      // Get the line height from the display element
      const lineHeight = parseInt(
        window.getComputedStyle(displayElement).lineHeight,
      );
      const maxHeight = lineHeight * maxLines;

      // Measure the natural height without line clamp
      const naturalHeight = measureElement.scrollHeight;

      setShowTextButton(naturalHeight > maxHeight);
    }
  }, [text, maxLines]);

  // Map maxLines to Tailwind line-clamp classes
  const getLineClampClass = (lines: number) => {
    const lineClampMap: Record<number, string> = {
      1: "line-clamp-1",
      2: "line-clamp-2",
      3: "line-clamp-3",
      4: "line-clamp-4",
      5: "line-clamp-5",
      6: "line-clamp-6",
    };
    return lineClampMap[lines] ?? "line-clamp-3";
  };

  return (
    <div>
      {/* Hidden element to measure natural height */}
      <div
        ref={measureRef}
        className="invisible absolute"
        style={{
          position: "absolute",
          top: "-9999px",
          left: "-9999px",
          visibility: "hidden",
          width: "100%",
        }}
      >
        <Typography size={size} className={className}>
          {text}
        </Typography>
      </div>

      {/* Visible element with conditional line clamp */}
      <div ref={textRef}>
        <Typography
          size={size}
          className={cn(
            className,
            isTextExpanded ? "" : getLineClampClass(maxLines),
          )}
        >
          {text}
        </Typography>
      </div>
      {showTextButton && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIsTextExpanded(!isTextExpanded);
          }}
          className="mt-2 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
        >
          {isTextExpanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
};
